import { theme } from 'antd';
import { removeEmptyOrNull } from 'library/helpers/utility';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { organisationActions } from 'reduxStore/organisations/slice';
import { Button, Flex, Form, Input, Modal, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const CreateOrganisationModal = ({ children, open, setOpen }) => {
	//Hooks
	const {
		token: { paddingLG, fontSizeSM, fontWeightStrong, colorTextDescription, marginXL, paddingSM, paddingXL }
	} = theme.useToken();
	const dispatch = useDispatch();
	const [form] = Form.useForm();

	// Redux state
	const { loadingCreateOrganisation, completeAddOrganisation } = useSelector((state) => state.Organisations);

	// Local state
	const [internalVisible, internalSetVisible] = useState(false);
	const visible = open || internalVisible;
	const setVisible = setOpen || internalSetVisible;

	// Handle cancel
	const handleCancel = useCallback(() => {
		form.resetFields();
		setVisible(false);
	}, [form, setVisible]);

	// ✅ Close modal and reset form when loadingCreateOrganisation changes
	useEffect(() => {
		if (completeAddOrganisation) handleCancel();
	}, [completeAddOrganisation, handleCancel]);

	// Handle form submission
	const handleSubmit = (values) => {
		const data = removeEmptyOrNull(values);
		dispatch(organisationActions.createOrganisation(data));
	};

	//Permission check for adding an organisation
	const isPermittedCreateOrg = useManagePermittedRoles('addOrg');

	return (
		<>
			<Tooltip title={isPermittedCreateOrg ? 'Create new organisation' : "You don't have permission"}>
				{children ? (
					children
				) : (
					<Button
						type='primary'
						onClick={() => setVisible(true)}
						disabled={!isPermittedCreateOrg}
						block
						loading={loadingCreateOrganisation}
					>
						Create Organisation
					</Button>
				)}
			</Tooltip>

			<Modal
				open={visible}
				onCancel={() => {
					if (!loadingCreateOrganisation) {
						handleCancel();
					}
				}}
				width={425}
				centered
				closeIcon={false}
				destroyOnClose
				styles={{ content: { padding: `${paddingLG}px ${paddingXL}px` } }}
				footer={[
					<Flex gap={paddingSM} align='center' justify='flex-end' style={{ marginTop: paddingXL }}>
						<Button key='cancel' onClick={handleCancel} disabled={loadingCreateOrganisation}>
							Cancel
						</Button>
						<Button
							type='primary'
							htmlType='submit'
							loading={loadingCreateOrganisation}
							form='create-org-form'
							key='submit'
						>
							Create Organisation
						</Button>
					</Flex>
				]}
			>
				<Title level={4} style={{ margin: 0 }}>
					Create Organisation
				</Title>

				<Text
					style={{
						lineHeight: '20px',
						fontSize: fontSizeSM,
						color: colorTextDescription,
						display: 'block',
						marginBottom: 12
					}}
				>
					Create a new organisation to represent a business,
					<br /> organisation, or client.
				</Text>

				<Form
					id='create-org-form'
					form={form}
					layout='vertical'
					onFinish={handleSubmit}
					style={{ marginBottom: marginXL }}
					disabled={loadingCreateOrganisation} // ⬅️ Block form while loading
				>
					<Form.Item
						name='organisationName'
						label='Organisation Name'
						rules={fieldsValidator.Name}
						style={{ marginTop: 24, marginBottom: 12, fontWeight: fontWeightStrong }}
						required={false}
					>
						<Input placeholder='Enter organisation name' />
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

export default CreateOrganisationModal;
